import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import storyCardUi from "../../assets/images/feature-announcements/story-card-ui.gif";
import settingsIconUi from "../../assets/images/feature-announcements/settings-icon-ui.gif";
import storyFeaturesOverview from "../../assets/images/feature-announcements/story-features-overview.png";
import { FEATURE_ANNOUNCEMENT_KEYS, type FeatureAnnouncementKey } from "../../constants/featureAnnouncements";
import { getFeatureViews } from "../../store/actions/user";
import type { AppDispatch } from "../../store/state/rootStore";
import type { RootState } from "../../store/state/rootReducer";
import { markAnnouncementSeen, setAnnouncements } from "../../store/state/featureAnnouncements/reducer";

import { FeatureAnnouncementModal } from "./FeatureAnnouncementModal";
import type { FeatureAnnouncementSlide } from "./FeatureAnnouncementModal";

interface FeatureAnnouncementDefinition {
  featureKey: FeatureAnnouncementKey;
  version: string;
  title: string;
  publishedAt: string;
  slides: FeatureAnnouncementSlide[];
}

const featureAnnouncements: FeatureAnnouncementDefinition[] = [
  {
    featureKey: FEATURE_ANNOUNCEMENT_KEYS.STORY_CARD_SETTINGS_UI,
    version: "1.0.0",
    title: "Story card and Settings updates",
    publishedAt: "2026-09-16T00:00:00.000Z",
    slides: [
      {
        title: "We've made a few changes to TheirStory",
        media: { type: "image", src: storyFeaturesOverview, alt: "TheirStory feature icons" },
      },
      {
        title: "We've moved some of your buttons.",
        description:
          "Find Share, Export, and Transcript, as well as the triple-dot menu by hovering over the thumbnail.",
        media: { type: "image", src: storyCardUi, alt: "Updated TheirStory story card interface" },
      },
      {
        title: "We did a little rearranging for the Settings.",
        description: "The gear now exists at the top of the page, next to your project name.",
        media: { type: "image", src: settingsIconUi, alt: "Updated TheirStory project settings icon" },
      },
    ],
  },
];

const getLatestAnnouncement = () =>
  [...featureAnnouncements].sort(
    (firstAnnouncement, secondAnnouncement) =>
      Date.parse(secondAnnouncement.publishedAt) - Date.parse(firstAnnouncement.publishedAt)
  )[0];

const getAnnouncementStatusKey = ({ featureKey, version }: { featureKey: string; version: string }) =>
  `${featureKey}:${version}`;

export const UiUpdatesAnnouncement = () => {
  const dispatch = useDispatch<AppDispatch>();
  const authenticated = useSelector((state: RootState) => state.auth.authenticated);
  const userId = useSelector((state: RootState) => state.auth.user_id);
  const requestedAnnouncement = useSelector((state: RootState) => state.featureAnnouncements.requestedAnnouncement);
  const announcements = useSelector((state: RootState) => state.featureAnnouncements.announcements);
  const latestAnnouncement = getLatestAnnouncement();
  const selectedAnnouncement =
    featureAnnouncements.find(
      (announcement) =>
        announcement.featureKey === requestedAnnouncement?.featureKey &&
        announcement.version === requestedAnnouncement?.version
    ) ?? latestAnnouncement;
  const selectedSummary = announcements.find(
    (announcement) =>
      announcement.featureKey === selectedAnnouncement?.featureKey &&
      announcement.version === selectedAnnouncement?.version
  );

  useEffect(() => {
    if (!authenticated || !userId) {
      dispatch(setAnnouncements([]));
      return undefined;
    }

    let isMounted = true;
    void dispatch(getFeatureViews()).then((viewedAnnouncements) => {
      if (!isMounted) return;

      const viewedAnnouncementKeys = new Set(viewedAnnouncements.map(getAnnouncementStatusKey));
      const announcementStatuses = featureAnnouncements.map((announcement) => ({
        featureKey: announcement.featureKey,
        version: announcement.version,
        title: announcement.title,
        publishedAt: announcement.publishedAt,
        seen: viewedAnnouncementKeys.has(getAnnouncementStatusKey(announcement)),
      }));

      dispatch(setAnnouncements(announcementStatuses));
    });

    return () => {
      isMounted = false;
    };
  }, [authenticated, dispatch, userId]);

  const handleAnnouncementViewed = useCallback(() => {
    if (!selectedAnnouncement) return;

    dispatch(
      markAnnouncementSeen({
        featureKey: selectedAnnouncement.featureKey,
        version: selectedAnnouncement.version,
      })
    );
  }, [dispatch, selectedAnnouncement]);

  if (!authenticated || !userId || !selectedAnnouncement || !selectedSummary) return null;

  return (
    <FeatureAnnouncementModal
      featureKey={selectedAnnouncement.featureKey}
      version={selectedAnnouncement.version}
      slides={selectedAnnouncement.slides}
      hasBeenSeen={selectedSummary.seen}
      onViewed={handleAnnouncementViewed}
    />
  );
};
